"use client";

import { useState } from "react";
import dynamic from "next/dynamic";
import { cdn } from "@/lib/cdn";
import Image from "next/image";
import Link from "next/link";
import GradientAvatar from "@/app/components/GradientAvatar";
import { useAuth } from "@/hooks/useAuth";
import { Users, Crown } from "lucide-react";
import { useMostCopiedQuery, type MostCopiedTrader } from "@/hooks/useMostCopiedQuery";
import { useT } from "@/hooks/useT";

const CopyTraderModal = dynamic(
  () => import("@/app/traders/components/CopyTraderModal"),
  { ssr: false }
);

function formatPnL(pnl: number): string {
  const abs = Math.abs(pnl);
  if (abs >= 1000000) return `$${(abs / 1000000).toFixed(1)}M`;
  if (abs >= 1000) return `$${(abs / 1000).toFixed(1)}K`;
  return `$${abs.toFixed(0)}`;
}

export default function MostCopied() {
  const t = useT();
  const { isLoggedIn, isReady, currentUserId, currentWalletAddress } = useAuth();
  const { data: tradersRaw = [], isLoading: loading } = useMostCopiedQuery();
  // Filter out the current user — copying yourself is a no-op that just
  // confuses the engine and the UI. Match on FF id (when present) and on
  // wallet (covers users who haven't claimed a FF account on this device).
  const ownWallet = currentWalletAddress?.toLowerCase();
  const traders = tradersRaw.filter((t) => {
    if (currentUserId && t.id && t.id === currentUserId) return false;
    const w = (t.wallet_address || t.address || '').toLowerCase();
    if (ownWallet && w && w === ownWallet) return false;
    return true;
  });
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [selectedTrader, setSelectedTrader] = useState<{
    username: string;
    wallet: string;
    avatar: string;
  } | null>(null);

  if (!isReady || !isLoggedIn) {
    return null;
  }

  const cardClasses = "bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-5";

  if (loading) {
    return (
      <div className={cardClasses}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-satoshi font-bold text-gray-900 dark:text-white text-sm flex items-center gap-1.5"><Crown className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />{t("mostCopied.title")}</h3>
          <span className="text-[10px] font-satoshi text-gray-400 dark:text-gray-500">{t("mostCopied.window24h")}</span>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse flex gap-3 items-center">
              <div className="w-10 h-10 bg-gray-100 dark:bg-[#0f0f12] rounded-[5px] flex-shrink-0" />
              <div className="flex-1">
                <div className="h-3.5 bg-gray-100 dark:bg-[#0f0f12] rounded w-20 mb-1.5" />
                <div className="h-2.5 bg-gray-100 dark:bg-[#0f0f12] rounded w-14" />
              </div>
              <div className="w-14 h-8 bg-gray-100 dark:bg-[#0f0f12] rounded-[5px]" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (traders.length === 0) {
    return (
      <div className={cardClasses}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-satoshi font-bold text-gray-900 dark:text-white text-sm flex items-center gap-1.5"><Crown className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />{t("mostCopied.title")}</h3>
          <span className="text-[10px] font-satoshi text-gray-400 dark:text-gray-500">{t("mostCopied.window24h")}</span>
        </div>
        <p className="text-xs font-satoshi text-gray-500 dark:text-gray-400 text-center py-6">
          {t("mostCopied.noTraders")}
        </p>
      </div>
    );
  }

  const handleCopy = (trader: MostCopiedTrader) => {
    const wallet = trader.wallet_address || trader.address;
    if (!wallet) return;
    setSelectedTrader({
      username: trader.username,
      wallet: wallet,
      avatar: trader.profile_image || '',
    });
    setShowCopyModal(true);
  };

  return (
    <div className={cardClasses}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-satoshi font-bold text-gray-900 dark:text-white text-sm flex items-center gap-1.5"><Crown className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />{t("mostCopied.title")}</h3>
        <span className="text-[10px] font-satoshi text-gray-400 dark:text-gray-500">{t("mostCopied.window24h")}</span>
      </div>
      <div className="space-y-1">
        {traders.map((trader) => {
          const pnl = parseFloat(trader.pnl) || 0;
          const isPositive = pnl >= 0;

          return (
            <div key={trader.id} className="flex gap-2.5 items-center p-2 -mx-2 rounded-[5px] hover:bg-gray-50/80 dark:hover:bg-white/[0.04] transition-colors">
              <Link href={`/traders/${trader.username}`} prefetch={false} className="flex-shrink-0">
                <div className="w-10 h-10 rounded-[5px] ring-1 ring-gray-200/80 dark:ring-white/[0.08] overflow-hidden">
                  {trader.profile_image && !trader.profile_image.includes('frenpfp') ? (
                    <Image
                      src={trader.profile_image}
                      alt={trader.username}
                      width={40}
                      height={40}
                      sizes="40px"
                      className="w-full h-full rounded-[5px] object-cover"
                    />
                  ) : (
                    <GradientAvatar seed={trader.wallet_address || trader.address || trader.username} size={40} className="w-full h-full !rounded-[5px]" />
                  )}
                </div>
              </Link>
              <div className="flex-1 min-w-0">
                <Link
                  href={`/traders/${trader.username}`}
                  prefetch={false}
                  className="block text-[13px] font-satoshi font-semibold text-gray-900 dark:text-white hover:text-gray-600 dark:hover:text-gray-300 transition-colors truncate leading-tight"
                >
                  @{trader.username}
                </Link>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {trader.copiers_count ? (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-satoshi text-gray-400 dark:text-gray-500">
                      <Users className="w-2.5 h-2.5" />
                      {trader.copiers_count}
                    </span>
                  ) : null}
                  {pnl !== 0 && (
                    <span className={`text-[10px] font-bold tabular-nums px-1 py-px rounded ${
                      isPositive
                        ? "text-emerald-600 dark:text-emerald-400 bg-emerald-500/10"
                        : "text-red-600 dark:text-red-400 bg-red-500/10"
                    }`}>
                      {isPositive ? "+" : "-"}{formatPnL(pnl)}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => handleCopy(trader)}
                disabled={!trader.wallet_address && !trader.address}
                className="px-3.5 py-1.5 rounded-[5px] text-[11px] font-inter-tight font-bold bg-gray-900 dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors duration-200 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("mostCopied.copyButton")}
              </button>
            </div>
          );
        })}
      </div>

      {selectedTrader && (
        <CopyTraderModal
          isOpen={showCopyModal}
          onClose={() => {
            setShowCopyModal(false);
            setSelectedTrader(null);
          }}
          trader={selectedTrader}
        />
      )}
    </div>
  );
}
